import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import cn from 'classnames';
import ABTestVerification from 'features/auth/ABTestVerification';
import CouponWithBets from 'features/coupon/container/couponWithBets';
import AccountVerification from 'features/customerPanel/accountVerification/container';
import fetchInGameCouponCount from 'features/customerPanel/cpCoupons/store/cpCoupons.action';
import { NativeNotificationManager } from 'features/NativeNotification/NativeNotificationManager';
import { t } from 'i18next';
import Breadcrumbs from 'shared/components/breadcrumbs/breadcrumbs';
import Navigation from 'shared/components/navigation/navigation';
import NavigationPopular from 'shared/components/navigation/navigationPopular';
import PrimaryCouponOverviewPanel from 'shared/components/PrimaryCouponOverivewPanel';
import SearcherDesktop from 'shared/components/searcherDesktop/searcherDesktop';
import { APP_URL, SectionNavigation, THREE_COLUMN_URL } from 'shared/constants/common';
import { useDeviceDetection } from 'shared/hooks/useDeviceDetection';
import { useNewMobileUiManager } from 'shared/hooks/useNewMobileUiManager';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { BETSType, CookieState, State } from 'shared/interface';
import { isLoggedin, isMobile } from 'shared/util/utility';
import BottomPanel from './bottomPanel';
import Footer from './footer';
import Header from './header/header';
import PWABanner from './pwaBanner';
import RenderComponents from './renderComponents';

interface MyProps {
	children?: React.ReactNode;
}

declare const window: any;
const Layout: React.FunctionComponent<MyProps> = (props) => {
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const dispatch = useDispatch();
	const device = useDeviceDetection();
	const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
	const notShowCookie = useSelector<State, CookieState>((state) => state.cookie.notShowCookie);
	const { isViewActive, isMainView, changeNavigationView } = useSectionNavigation();
	const showNewUI = useNewMobileUiManager();
	const numOfSlipsInBetslip = bets.outcomes.length + bets.blocks.flat().length;

	const [isShowInstall, setIsShowInstall] = useState<boolean>(localStorage.getItem('isShowBanner') === 'true');
	const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
	const [isFullScreenToggled, setIsFullScreenToggled] = useState(false);

	const bannerCount = localStorage.getItem('bannerCount');
	const showBanner =
		isShowInstall &&
		location.pathname === APP_URL.home &&
		isViewActive(SectionNavigation.HOME) &&
		Number(bannerCount) < 3 &&
		device === 'handheld';
	const isPopupWithRoute =
		[APP_URL.deposit, APP_URL.login, APP_URL.forgotPassword, APP_URL.resetPassword].includes(location.pathname) ||
		location.pathname.includes(APP_URL.resetPassword) ||
		location.pathname.startsWith(APP_URL.register) ||
		location.pathname.startsWith(APP_URL.verification) ||
		isViewActive(SectionNavigation.ACCOUNT_VERIFICATION_METHOD);
	const isShowBreadcrumbs =
		![APP_URL.pageNotFound, APP_URL.home, APP_URL.map, APP_URL.virtualSports, APP_URL.myBet, APP_URL.kuponKibica].includes(location.pathname) &&
		!isPopupWithRoute &&
		(isMainView() || (!isMobile() && isViewActive(SectionNavigation.USER)));

	const calculateHeight = useCallback(() => {
		const height = window.innerHeight;

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		document.getElementsByTagName('body')[0]!.style.setProperty('--screen-height', `${window.innerHeight}px`);
		if (window.innerHeight !== screenHeight) {
			setScreenHeight(height);
		}
	}, [screenHeight]);

	const onClickClose = () => {
		let bannerCount: any = Number(localStorage.getItem('bannerCount'));
		setIsShowInstall(false);

		bannerCount += 1;
		localStorage.setItem('bannerCount', String(bannerCount));
		localStorage.setItem('isShowBanner', 'false');
	};

	const onClickInstall = () => {
		window.deferredPrompt.prompt();
		window.deferredPrompt.userChoice.then((choiceResult: any) => {
			if (choiceResult.outcome === 'accepted') {
				setIsShowInstall(false);
				localStorage.setItem('isShowBanner', 'false');
			} else {
				setIsShowInstall(true);
			}
		});
	};

	const isThreeColumnsLayout = () => {
		// promotion and horse race pages routes are "different" from other pages. They use part of homepage route so includes is false positive here.
		if ([APP_URL.promotion, APP_URL.horseRaceGame].find((url: string) => location.pathname.includes(url))) return false;

		return THREE_COLUMN_URL.find((url: string) => location.pathname.includes(url));
	};

	const isBetMatch = location.pathname.includes(APP_URL.betMatch);
	const isDailyChallenge = location.pathname.includes(APP_URL.dailyChallenge);
	const isPlayerProfile = location.pathname.includes(APP_URL.playerProfile);
	const renderFooter = () =>
		((!isPopupWithRoute && isViewActive(SectionNavigation.HOME) && !isDailyChallenge && !isBetMatch && !isPlayerProfile) ||
			!isMobile() ||
			(isMobile() && isViewActive(SectionNavigation.COUPON_LIST))) && <Footer />;

	const renderDesktopView = (showBanner: boolean, isShowBreadcrumbs: boolean) => {
		return isThreeColumnsLayout() ? (
			<div className={`betfan-layout-wrapper flex align__items--start justify__content--between layout-wrapper-desktop`}>
				<div className="right-side-outside">
					<div className="right-side-panel flex align__items--center justify__content--center">
						<SearcherDesktop />
						{!location.pathname.includes(APP_URL.live) && <NavigationPopular title={t('popular')} />}
						<Navigation title={t('disciplines')} />
					</div>
				</div>
				<main className={`layout ${showBanner ? 'pt--118' : ''} layout-desktop`}>
					<div className="layoutContent">
						{isShowBreadcrumbs && <Breadcrumbs />}
						{props.children}
					</div>
					{renderFooter()}
				</main>
				<div className="sticky top-[80px] fullhd:top-[100px] flex flex-col">
					<CouponWithBets changeNavigationView={changeNavigationView} />
				</div>
			</div>
		) : (
			<div className={`betfan-layout-wrapper flex align__items--start justify__content--between layout-wrapper-desktop_single-column`}>
				<main className={`layout ${showBanner ? 'pt--118' : ''} layout-desktop`}>
					<div className="layoutContent">
						{isShowBreadcrumbs && <Breadcrumbs />}
						{(isMainView() ||
							isViewActive(SectionNavigation.USER) ||
							isViewActive(SectionNavigation.REGISTRATION) ||
							isViewActive(SectionNavigation.ACCOUNT_VERIFICATION)) &&
							props.children}
					</div>
					{renderFooter()}
				</main>
			</div>
		);
	};

	useEffect(() => {
		window.addEventListener('beforeinstallprompt', (e: any) => {
			e.preventDefault();
			window.deferredPrompt = e;

			// TODO: GET RID OF THIS, WE HAVE ISSUE FOR THAT, NOW I COMMENTED IT OUT TO MAKE IT WORK
			// if (window.deferredPrompt.prompt()) {
			// 	localStorage.setItem('isShowBanner', 'true');
			// 	setIsShowInstall(localStorage.getItem('isShowBanner') === 'true');
			// }
		});

		window.addEventListener('appinstalled', () => {
			localStorage.setItem('isShowBanner', 'false');
			setIsShowInstall(false);
		});

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		document.getElementsByTagName('body')[0]!.style.setProperty('--screen-height', `${window.innerHeight}px`);
		window.addEventListener('resize', calculateHeight);
		document.addEventListener('fullscreenchange', () => setIsFullScreenToggled(true));

		if (isLoggedin() && !isFullScreenToggled) {
			dispatch(fetchInGameCouponCount());
		}

		return () => {
			window.removeEventListener('resize', calculateHeight);
			document.removeEventListener('fullscreenchange', () => setIsFullScreenToggled(false));
		};
	}, [calculateHeight, dispatch, isFullScreenToggled]);

	return (
		<>
			{showBanner && <PWABanner isShowInstall={isShowInstall} onClickInstall={onClickInstall} onClickClose={onClickClose} />}
			<Header
				isShowInstall={showBanner}
				numOfSlipsInBetslip={numOfSlipsInBetslip}
				isPopupWithRoute={isPopupWithRoute}
				isMobile={device === 'handheld'}
				{...props}
			/>
			{isViewActive(SectionNavigation.ACCOUNT_VERIFICATION_METHOD) && <ABTestVerification />}
			{isViewActive(SectionNavigation.ACCOUNT_VERIFICATION) && <AccountVerification />}
			<NativeNotificationManager />

			{device === 'desktop' && renderDesktopView(showBanner, isShowBreadcrumbs)}
			{device === 'handheld' && (
				<>
					<main
						className={`layout ${showBanner ? 'pt--118' : ''} ${notShowCookie || APP_URL.playerProfile ? '' : 'cookie--padding-bottom'}`}
					>
						{!showNewUI && isShowBreadcrumbs && <Breadcrumbs />}

						<div
							className={cn(
								'content-wrapper',
								{ 'content-wrapper--breadcrumbs': !showNewUI && isShowBreadcrumbs },
								{ hide: !(isPopupWithRoute || isMainView()) },
							)}
						>
							{props.children}
						</div>

						{!isPopupWithRoute &&
							isMainView() &&
							![APP_URL.cardGames, APP_URL.horseRaceGame].find((url: string) => location.pathname.includes(url)) && (
								<PrimaryCouponOverviewPanel />
							)}

						{!isPopupWithRoute && <RenderComponents />}

						{renderFooter()}
					</main>

					{!isPopupWithRoute && isMainView() && (
						<BottomPanel match={match} history={history} location={location} handleNavTab={changeNavigationView} {...props} />
					)}
				</>
			)}
		</>
	);
};

export default Layout;
